"use client";

import * as React from "react";
import { useClerk } from "@/shims/clerk-react";
import { useNavigate } from "react-router-dom";
import { formatShortRelativeTime } from "@/shared/lib/dates/date-fns-locale";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  EmptyState,
  RouteGuardLoading,
  StatusBadge,
} from "@ibada/ui";
import {
  resolveWorkspaceLandingHref,
  useAcceptInvitationMutation,
  useActiveWorkspaceSlug,
  useAuthMeQuery,
  useDeclineInvitationMutation,
  useMarkWorkspaceInvitationsViewedMutation,
  useSetDefaultWorkspaceMutation,
  type AuthMeResponse,
} from "@/features/auth";
import { useModuleAccessDeniedToast } from "@/features/auth/hooks/use-module-access-denied-toast";
import { useLocale, useTranslations } from "@/i18n";
import { useApiErrorToast } from "@/shared/hooks/use-api-error";
import { getBranchDisplayLabel } from "@/shared/lib/branch/branch-display";
import { BadgeCheckIcon, CheckIcon, MailIcon, StarIcon } from "lucide-react";
import { useIsMobile } from "@/shared/hooks/use-mobile";
import { MobileFormRow, MobileFormSection } from "@/shared/components/mobile-form";
import { toast } from "sonner";

type WorkspaceAccessEntry = AuthMeResponse["workspaceAccess"][number];
type InvitationSummary = AuthMeResponse["pendingInvitations"][number];
type ProfileTranslations = ReturnType<typeof useTranslations>;

function formatEnumLabel(value: string | null | undefined): string {
  if (!value) return "-";
  return value
    .toLowerCase()
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function buildInitials(firstName: string | null | undefined, lastName: string | null | undefined): string {
  const first = firstName?.trim().charAt(0) ?? "";
  const last = lastName?.trim().charAt(0) ?? "";
  return `${first}${last}`.trim().toUpperCase() || "IC";
}

function getWorkspaceLabel(access: WorkspaceAccessEntry): string {
  return access.workspace.displayName?.trim() || access.workspace.name || access.workspace.slug;
}

function getWorkspaceSubtitle(access: WorkspaceAccessEntry, t: ProfileTranslations, parishLabel: string) {
  const branchNames = Array.from(
    new Set(
      (access.roles ?? [])
        .filter((role) => role.roleScope === "BRANCH")
        .map((role) =>
          getBranchDisplayLabel({
            branchType: role.branch?.branchType,
            branchName: role.branch?.name,
            parishLabel,
            fallbackLabel: "",
          }).trim(),
        )
        .filter((name): name is string => Boolean(name)),
    ),
  );

  if (branchNames.length > 0) {
    return branchNames.join(" • ");
  }

  if (access.member?.branch?.name) {
    return t("workspace.branchContext", {
      branch: getBranchDisplayLabel({
        branchType: access.member.branch.branchType,
        branchName: access.member.branch.name,
        parishLabel,
        fallbackLabel: access.member.branch.name,
      }),
    });
  }

  return t("workspace.workspaceScope");
}

function getRoleSummary(access: WorkspaceAccessEntry): string {
  const roleNames = Array.from(
    new Set(
      (access.roles ?? [])
        .map((role) => role.roleName?.trim())
        .filter((roleName): roleName is string => Boolean(roleName)),
    ),
  );

  if (roleNames.length > 0) {
    return roleNames.join(", ");
  }

  return formatEnumLabel(access.workspaceRole);
}

function InvitationStatusBadge({ expiresAt, locale }: { expiresAt: string; locale: string }) {
  return (
    <Badge variant='outline' className='border-amber-500/30 px-1.5 text-amber-700 dark:text-amber-300'>
      {formatShortRelativeTime(expiresAt, locale)}
    </Badge>
  );
}

/** A label/value row, styled to sit cleanly inside the Clerk "Manage account" modal. */
function ProfileRow({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex items-center justify-between gap-4 px-4 py-2.5'>
      <dt className='text-sm text-muted-foreground'>{label}</dt>
      <dd className='truncate text-sm font-medium text-foreground'>{value}</dd>
    </div>
  );
}

/**
 * Profile content rendered as a custom tab inside Clerk's "Manage account" modal (no standalone /profile
 * page). Identity summary + workspace memberships + invitations, styled to match Clerk's own elements.
 * Switching a workspace closes the modal and lands inside the chosen workspace (mirrors the header switcher).
 */
export function AccountProfilePanel() {
  const t = useTranslations("accountProfile");
  const toastError = useApiErrorToast("accountProfile");
  const tCommon = useTranslations("common");
  const tModuleDenied = useTranslations("dashboard.admin");
  useModuleAccessDeniedToast(tModuleDenied("moduleAccessDenied"));
  const locale = useLocale();
  const navigate = useNavigate();
  const clerk = useClerk();
  const activeWorkspaceSlug = useActiveWorkspaceSlug();
  const { data: authMe, isLoading } = useAuthMeQuery();
  const acceptInvitation = useAcceptInvitationMutation();
  const declineInvitation = useDeclineInvitationMutation();
  const { mutate: markInvitationsViewed } = useMarkWorkspaceInvitationsViewedMutation();
  const setDefaultWorkspaceMutation = useSetDefaultWorkspaceMutation();
  const isMobile = useIsMobile();

  const currentWorkspaceAccess = React.useMemo(() => {
    if (!authMe?.workspaceAccess?.length) return null;
    if (activeWorkspaceSlug) {
      const exact = authMe.workspaceAccess.find((access) => access.workspace.slug === activeWorkspaceSlug);
      if (exact) return exact;
    }

    return authMe.workspaceAccess.find((access) => access.status === "ACTIVE") ?? authMe.workspaceAccess[0] ?? null;
  }, [activeWorkspaceSlug, authMe?.workspaceAccess]);

  const currentWorkspaceName = currentWorkspaceAccess ? getWorkspaceLabel(currentWorkspaceAccess) : t("summary.none");
  const parishLabel = tCommon("scope.parish");
  const defaultWorkspaceId = authMe?.user.defaultWorkspaceId ?? null;
  const unviewedInvitationIds = React.useMemo(
    () =>
      (authMe?.pendingInvitations ?? [])
        .filter((invitation) => !invitation.isSystemInvite && !invitation.viewedAt)
        .map((invitation) => invitation.id),
    [authMe?.pendingInvitations],
  );

  React.useEffect(() => {
    if (unviewedInvitationIds.length === 0) return;
    markInvitationsViewed({ invitationIds: unviewedInvitationIds });
  }, [markInvitationsViewed, unviewedInvitationIds]);

  const dateTimeFormatter = React.useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        dateStyle: "medium",
        timeStyle: "short",
      }),
    [locale],
  );

  const handleWorkspaceOpen = React.useCallback(
    (access: WorkspaceAccessEntry) => {
      if (!authMe || !access.workspace?.id) return;
      // Land INSIDE the chosen workspace (persist the target branch), then close the Clerk modal so the
      // navigation is visible. Mirrors the header workspace switcher.
      clerk.closeUserProfile?.();
      navigate(resolveWorkspaceLandingHref(authMe, access));
    },
    [authMe, clerk, navigate],
  );

  const handleAcceptInvitation = React.useCallback(
    async (invitation: InvitationSummary) => {
      if (!invitation.workspaceId) {
        toast.error(t("invitations.errors.missingWorkspace"));
        return;
      }

      try {
        await acceptInvitation.mutateAsync({
          code: invitation.code,
          workspaceId: invitation.workspaceId,
        });
        toast.success(
          t("invitations.toasts.accepted", {
            workspace: invitation.workspace?.displayName || invitation.workspace?.name || t("summary.workspace"),
          }),
        );
      } catch (error) {
        toastError(error);
      }
    },
    [acceptInvitation, t, toastError],
  );

  const handleDeclineInvitation = React.useCallback(
    async (invitation: InvitationSummary) => {
      if (!invitation.workspaceId) {
        toast.error(t("invitations.errors.missingWorkspace"));
        return;
      }

      try {
        await declineInvitation.mutateAsync({
          code: invitation.code,
          workspaceId: invitation.workspaceId,
        });
        toast.success(
          t("invitations.toasts.declined", {
            workspace: invitation.workspace?.displayName || invitation.workspace?.name || t("summary.workspace"),
          }),
        );
      } catch (error) {
        toastError(error);
      }
    },
    [declineInvitation, t, toastError],
  );

  const handleSetDefaultWorkspace = React.useCallback(
    async (access: WorkspaceAccessEntry) => {
      try {
        await setDefaultWorkspaceMutation.mutateAsync({
          workspaceId: access.workspace.id,
        });
        toast.success(
          t("workspace.defaultUpdated", {
            workspace: getWorkspaceLabel(access),
          }),
        );
      } catch (error) {
        toastError(error);
      }
    },
    [setDefaultWorkspaceMutation, t, toastError],
  );

  if (isLoading) {
    return <RouteGuardLoading />;
  }

  if (!authMe) {
    return (
      <EmptyState
        title={t("states.unavailable.title")}
        description={t("states.unavailable.description")}
        tone='danger'
      />
    );
  }

  const userName = `${authMe.user.firstName} ${authMe.user.lastName}`.trim();

  // Phone: the person's identity is already at the top of "Manage account", so this page is only their spaces
  // (tap one to open it, ⋯ to make it the default), their invitations (Refuser / Accepter pills) and the account's
  // dates, all as the app's grouped rows.
  if (isMobile) {
    return (
      <div className='flex flex-col gap-5'>
        <MobileFormSection title={t("workspace.title")} footer={authMe.workspaceAccess.length === 0 ? t("workspace.empty.description") : undefined}>
          {authMe.workspaceAccess.map((access) => {
            const workspaceLabel = getWorkspaceLabel(access);
            const isCurrentWorkspace = access.workspace.slug === (activeWorkspaceSlug ?? currentWorkspaceAccess?.workspace.slug);
            const isDefaultWorkspace = access.workspace.id === defaultWorkspaceId;
            const canOpenWorkspace = access.status === "ACTIVE";
            const detail = [getRoleSummary(access), getWorkspaceSubtitle(access, t, parishLabel)].filter(Boolean).join(" · ");
            return (
              <div key={access.id} className='flex min-h-16 items-center gap-1 pr-1'>
                <button
                  type='button'
                  disabled={!canOpenWorkspace || isCurrentWorkspace}
                  onClick={() => handleWorkspaceOpen(access)}
                  className='flex min-w-0 flex-1 items-center gap-3 py-2.5 pl-4 text-left active:bg-muted disabled:opacity-100'
                >
                  <Avatar className='size-10 shrink-0 rounded-xl'>
                    <AvatarImage src={access.workspace.logoUrl ?? undefined} alt='' className='rounded-xl object-cover' />
                    <AvatarFallback className='rounded-xl bg-primary/10 text-xs font-extrabold text-primary'>
                      {workspaceLabel.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
                    <span className='flex min-w-0 items-center gap-1.5'>
                      <span className='truncate text-[15px] font-extrabold'>{workspaceLabel}</span>
                      {isDefaultWorkspace ? <StarIcon aria-label={t("workspace.defaultBadge")} className='size-3.5 shrink-0 fill-amber-400 text-amber-400' /> : null}
                    </span>
                    <span className='truncate text-xs font-semibold text-muted-foreground'>{detail}</span>
                  </span>
                  {isCurrentWorkspace ? <CheckIcon aria-label={t("workspace.currentBadge")} className='size-5 shrink-0 text-primary' strokeWidth={3} /> : null}
                </button>
                {!isDefaultWorkspace && canOpenWorkspace ? (
                  // The row's one action, so a star to tap rather than a ⋯ hiding a single item.
                  <button
                    type='button'
                    aria-label={`${t("workspace.setDefaultAction")}: ${workspaceLabel}`}
                    title={t("workspace.setDefaultAction")}
                    onClick={() => void handleSetDefaultWorkspace(access)}
                    className='inline-flex size-11 shrink-0 items-center justify-center rounded-full text-muted-foreground/60 active:bg-muted'
                  >
                    <StarIcon aria-hidden className='size-5' />
                  </button>
                ) : (
                  <span className='w-10 shrink-0' />
                )}
              </div>
            );
          })}
        </MobileFormSection>

        {authMe.pendingInvitations.length > 0 ? (
          <MobileFormSection title={t("invitations.pendingTitle")}>
            {authMe.pendingInvitations.map((invitation) => {
              const busy =
                (acceptInvitation.isPending && acceptInvitation.variables?.code === invitation.code) ||
                (declineInvitation.isPending && declineInvitation.variables?.code === invitation.code);
              const workspaceName = invitation.workspace?.displayName?.trim() || invitation.workspace?.name || t("summary.workspace");
              return (
                <div key={invitation.id} className='flex flex-col gap-2.5 px-4 py-3'>
                  <span className='flex min-w-0 flex-col gap-0.5'>
                    <span className='truncate text-[15px] font-extrabold'>{workspaceName}</span>
                    <span className='truncate text-xs font-semibold text-muted-foreground'>
                      {t("invitations.roleLine", { role: formatEnumLabel(invitation.roleKey) })} · {formatShortRelativeTime(invitation.expiresAt, locale)}
                    </span>
                  </span>
                  <span className='grid grid-cols-2 gap-2'>
                    <button
                      type='button'
                      disabled={busy}
                      onClick={() => void handleDeclineInvitation(invitation)}
                      className='h-10 rounded-full bg-muted text-sm font-extrabold text-foreground disabled:opacity-50'
                    >
                      {t("invitations.actions.decline")}
                    </button>
                    <button
                      type='button'
                      disabled={busy}
                      onClick={() => void handleAcceptInvitation(invitation)}
                      className='h-10 rounded-full bg-primary text-sm font-extrabold text-primary-foreground disabled:opacity-50'
                    >
                      {t("invitations.actions.accept")}
                    </button>
                  </span>
                </div>
              );
            })}
          </MobileFormSection>
        ) : null}

        {authMe.expiredInvitations.length > 0 ? (
          <MobileFormSection title={t("invitations.expiredTitle")}>
            {authMe.expiredInvitations.map((invitation) => (
              <MobileFormRow
                key={invitation.id}
                label={invitation.workspace?.displayName?.trim() || invitation.workspace?.name || t("summary.workspace")}
                value={dateTimeFormatter.format(new Date(invitation.expiresAt))}
              />
            ))}
          </MobileFormSection>
        ) : null}

        <MobileFormSection>
          <MobileFormRow label={t("summary.accountStatus")} value={formatEnumLabel(authMe.user.status)} />
          <MobileFormRow
            label={t("summary.lastLogin")}
            value={authMe.user.lastLoginAt ? dateTimeFormatter.format(new Date(authMe.user.lastLoginAt)) : t("summary.never")}
          />
          <MobileFormRow label={t("summary.memberSince")} value={dateTimeFormatter.format(new Date(authMe.user.createdAt))} />
        </MobileFormSection>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <h1 className='text-xl font-semibold tracking-tight text-foreground'>{t("title")}</h1>

      <div className='border-t border-border' />

      {/* Identity */}
      <div className='flex items-center gap-4'>
        <Avatar className='size-14 border border-border/70'>
          <AvatarImage src={authMe.user.avatarUrl ?? undefined} alt={userName} />
          <AvatarFallback className='text-sm font-semibold'>
            {buildInitials(authMe.user.firstName, authMe.user.lastName)}
          </AvatarFallback>
        </Avatar>
        <div className='min-w-0 space-y-1'>
          <div className='flex flex-wrap items-center gap-2'>
            <p className='text-base font-semibold tracking-tight text-foreground'>{userName}</p>
            <StatusBadge status={authMe.user.status.toLowerCase()} />
          </div>
          <p className='inline-flex items-center gap-1.5 text-sm text-muted-foreground'>
            <MailIcon className='size-4 shrink-0' />
            <span className='truncate'>{authMe.user.email}</span>
          </p>
          {authMe.user.phone ? (
            <p className='inline-flex items-center gap-1.5 text-sm text-muted-foreground'>
              <BadgeCheckIcon className='size-4 shrink-0' />
              {authMe.user.phone}
            </p>
          ) : null}
        </div>
      </div>

      {/* Account details */}
      <dl className='divide-y divide-border rounded-lg border border-border'>
        <ProfileRow label={t("summary.accountStatus")} value={formatEnumLabel(authMe.user.status)} />
        <ProfileRow
          label={t("summary.lastLogin")}
          value={
            authMe.user.lastLoginAt
              ? dateTimeFormatter.format(new Date(authMe.user.lastLoginAt))
              : t("summary.never")
          }
        />
        <ProfileRow label={t("summary.currentWorkspace")} value={currentWorkspaceName} />
        <ProfileRow label={t("summary.memberSince")} value={dateTimeFormatter.format(new Date(authMe.user.createdAt))} />
      </dl>

      {/* Workspaces */}
      <section className='space-y-3'>
        <h2 className='text-sm font-medium text-foreground'>{t("workspace.title")}</h2>
        {authMe.workspaceAccess.length === 0 ? (
          <p className='text-sm text-muted-foreground'>{t("workspace.empty.description")}</p>
        ) : (
          <div className='divide-y divide-border rounded-lg border border-border'>
            {authMe.workspaceAccess.map((access) => {
              const workspaceLabel = getWorkspaceLabel(access);
              const workspaceSubtitle = getWorkspaceSubtitle(access, t, parishLabel);
              const roleSummary = getRoleSummary(access);
              const isCurrentWorkspace =
                access.workspace.slug === (activeWorkspaceSlug ?? currentWorkspaceAccess?.workspace.slug);
              const isDefaultWorkspace = access.workspace.id === defaultWorkspaceId;
              const canOpenWorkspace = access.status === "ACTIVE";
              const isSettingDefault =
                setDefaultWorkspaceMutation.isPending &&
                setDefaultWorkspaceMutation.variables?.workspaceId === access.workspace.id;

              return (
                <div
                  key={access.id}
                  className='flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between'
                >
                  <div className='flex min-w-0 items-center gap-3'>
                    <Avatar className='size-9 border border-border/70'>
                      <AvatarImage src={access.workspace.logoUrl ?? undefined} alt={workspaceLabel} />
                      <AvatarFallback className='text-xs'>{workspaceLabel.slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className='min-w-0 space-y-1'>
                      <div className='flex flex-wrap items-center gap-1.5'>
                        <span className='truncate text-sm font-medium text-foreground'>{workspaceLabel}</span>
                        {isCurrentWorkspace ? (
                          <Badge variant='secondary' className='px-1.5'>
                            {t("workspace.currentBadge")}
                          </Badge>
                        ) : null}
                        {isDefaultWorkspace ? (
                          <Badge variant='outline' className='px-1.5'>
                            {t("workspace.defaultBadge")}
                          </Badge>
                        ) : null}
                      </div>
                      <p className='truncate text-xs text-muted-foreground'>
                        {roleSummary} · {workspaceSubtitle}
                      </p>
                    </div>
                  </div>

                  <div className='flex shrink-0 items-center gap-2'>
                    {!isDefaultWorkspace && access.status === "ACTIVE" ? (
                      <Button
                        type='button'
                        size='sm'
                        variant='ghost'
                        onClick={() => void handleSetDefaultWorkspace(access)}
                        disabled={isSettingDefault}
                      >
                        {t("workspace.setDefaultAction")}
                      </Button>
                    ) : null}
                    <Button
                      type='button'
                      size='sm'
                      variant={isCurrentWorkspace ? "secondary" : "outline"}
                      onClick={() => handleWorkspaceOpen(access)}
                      disabled={!canOpenWorkspace}
                    >
                      {isCurrentWorkspace ? t("workspace.openCurrent") : t("workspace.switchAction")}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Pending invitations */}
      {authMe.pendingInvitations.length > 0 ? (
        <section className='space-y-3'>
          <h2 className='text-sm font-medium text-foreground'>{t("invitations.pendingTitle")}</h2>
          <div className='divide-y divide-border rounded-lg border border-border'>
            {authMe.pendingInvitations.map((invitation) => {
              const isAccepting = acceptInvitation.isPending && acceptInvitation.variables?.code === invitation.code;
              const isDeclining = declineInvitation.isPending && declineInvitation.variables?.code === invitation.code;
              const workspaceName =
                invitation.workspace?.displayName?.trim() || invitation.workspace?.name || t("summary.workspace");

              return (
                <div
                  key={invitation.id}
                  className='flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between'
                >
                  <div className='min-w-0 space-y-1'>
                    <div className='flex flex-wrap items-center gap-1.5'>
                      <span className='text-sm font-medium text-foreground'>{workspaceName}</span>
                      <InvitationStatusBadge expiresAt={invitation.expiresAt} locale={locale} />
                    </div>
                    <p className='text-xs text-muted-foreground'>
                      {t("invitations.roleLine", { role: formatEnumLabel(invitation.roleKey) })}
                    </p>
                  </div>
                  <div className='flex shrink-0 gap-2'>
                    <Button
                      type='button'
                      size='sm'
                      variant='ghost'
                      onClick={() => void handleDeclineInvitation(invitation)}
                      disabled={isAccepting || isDeclining}
                    >
                      {t("invitations.actions.decline")}
                    </Button>
                    <Button
                      type='button'
                      size='sm'
                      onClick={() => void handleAcceptInvitation(invitation)}
                      disabled={isAccepting || isDeclining}
                    >
                      {t("invitations.actions.accept")}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* Expired invitations */}
      {authMe.expiredInvitations.length > 0 ? (
        <section className='space-y-3'>
          <h2 className='text-sm font-medium text-foreground'>{t("invitations.expiredTitle")}</h2>
          <div className='divide-y divide-border rounded-lg border border-border'>
            {authMe.expiredInvitations.map((invitation) => {
              const workspaceName =
                invitation.workspace?.displayName?.trim() || invitation.workspace?.name || t("summary.workspace");

              return (
                <div key={invitation.id} className='flex items-center justify-between gap-3 p-3'>
                  <span className='text-sm font-medium text-foreground'>{workspaceName}</span>
                  <span className='text-xs text-muted-foreground'>
                    {t("invitations.expiresLine", {
                      date: dateTimeFormatter.format(new Date(invitation.expiresAt)),
                    })}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}
    </div>
  );
}
